import {
  AioExternalReplacements,
  AioExternalSingle,
  AioReplacement,
} from "../../additional/interface";
import { updateExternals } from "../../additional/updateExternals";
import { AitColumnRepeat, AitHeaderRowData, TlfViewModeConfig } from "./interface";
import { removeHeaderRowRepeatInfo } from "./removeRowRepeatInfo";
import { replaceHeaders } from "./replaceHeaders";
import { singleReplacements } from "./singleReplacements";

/**
 * Entry function to process headers with replacements.
 *
 * @param rows - Header rows to process
 * @param replacements - Replacements to process
 * @param defaultCellWidth - Default cell width for new cells
 * @param getTextFromT - Function to get text from generic type T
 * @param replaceTextInT - Function to replace text in generic type T
 * @param blankT - Blank value of generic type T
 * @param tlfViewMode - TLF view mode configuration
 * @param rowHeaderColumns - Number of row header columns
 * @param externalLists - Optional external lists for replacements
 * @param externalSingles - Optional external singles for single replacements
 * @returns Object containing processed header rows and column repeats
 */
export const repeatHeaders = <T>(
  rows: AitHeaderRowData<T>[],
  replacements: AioReplacement<T>[],
  defaultCellWidth: number,
  getTextFromT: (s: T) => string[],
  replaceTextInT: (s: T, oldPhrase: string, newPhrase: T) => T,
  blankT: T,
  tlfViewMode: TlfViewModeConfig,
  rowHeaderColumns: number,
  externalLists?: AioExternalReplacements<T>[],
  externalSingles?: AioExternalSingle<T>[],
): { rows: AitHeaderRowData<T>[]; columnRepeats: AitColumnRepeat[] } => {
  // Start with blank slate, need to strip repeat information every time!
  let newHeaderRows: AitHeaderRowData<T>[] = removeHeaderRowRepeatInfo(rows);
  let newColumnRepeats: AitColumnRepeat[] = Array.from(rows[rows.length - 1].cells.keys()).map(
    (n) => {
      return { columnIndex: n } as AitColumnRepeat;
    },
  );

  // Strip repeat data if flagged
  if (tlfViewMode.noRepeatProcessing)
    return {
      rows: newHeaderRows,
      columnRepeats: newColumnRepeats,
    };

  // Process replacements
  updateExternals(replacements, externalLists)?.forEach((rep) => {
    const afterReplacement = replaceHeaders(
      rowHeaderColumns,
      newHeaderRows,
      newColumnRepeats,
      defaultCellWidth,
      getTextFromT,
      replaceTextInT,
      blankT,
      rep,
      undefined,
    );
    newHeaderRows = afterReplacement.newHeaderRows;
    newColumnRepeats = afterReplacement.newColumnRepeats;
  });

  // Single post processing rep
  newHeaderRows = singleReplacements(externalSingles, newHeaderRows, replaceTextInT);

  return { rows: newHeaderRows, columnRepeats: newColumnRepeats };
};
